import { ImgHTMLAttributes } from 'react';

import { cva, VariantProps } from 'cva';

import { BodyProps } from '../Typography/Body/Body.styles';
import { NumericProps } from '../Typography/Numeric/Numeric.styles';

type ThumbnailType = 'icon' | 'image' | 'counter' | 'label' | 'asset' | 'currency';

export type ThumbnailProps = {
  type: ThumbnailType;
  icon?: React.FC<ImgHTMLAttributes<SVGElement>>;
  count?: number;
  textColor?: NumericProps['color'] | BodyProps['color'];
  label?: string;
  bgColor?: string;
  iconColor?: string;
  assetCode?: string;
  className?: string;
} & (
  | { type: 'icon'; icon: React.FC<ImgHTMLAttributes<SVGElement>> }
  | { type: 'image'; src: string }
  | { type: 'counter'; count: number; textColor?: NumericProps['color'] }
  | { type: 'label'; label: string; textColor?: BodyProps['color'] }
  | { type: 'asset'; assetCode?: string }
) &
  VariantProps<typeof thumbnailVariants>;

export const getCounterTextSize = (size: ThumbnailProps['size']): NumericProps['size'] => {
  switch (size) {
    case 'xxxlg':
      return '5xl';
    case 'xxlg':
      return 'xxlarge';
    case 'xlg':
      return 'xlarge';
    case 'lg':
      return 'large';
    case 'md':
      return 'medium';
    case 'sm':
      return 'small';
    case 'xsm':
      return 'xsmall';
    case 'xxsm':
      return 'xxsmall';
  }
};

export const getLabelTextSize = (size: ThumbnailProps['size']): BodyProps['size'] => {
  switch (size) {
    case 'xxxlg':
      return 'xlarge';
    case 'xxlg':
      return 'xlarge';
    case 'xlg':
      return 'xlarge';
    case 'lg':
      return 'large';
    case 'md':
      return 'medium';
    case 'sm':
      return 'small';
    case 'xsm':
      return 'xsmall';
    case 'xxsm':
      return 'xsmall';
  }
};

export const getAssetIconSize = (size: ThumbnailProps['size']): string => {
  switch (size) {
    case 'xxxlg':
      return 'text-[64px]';
    case 'xxlg':
      return 'text-[48px]';
    case 'xlg':
      return 'text-[32px]';
    case 'lg':
      return 'text-[24px]';
    case 'md':
    case undefined:
    case null:
      return 'text-[20px]';
    case 'sm':
      return 'text-[16px]';
    case 'xsm':
      return 'text-[12px]';
    case 'xxsm':
      return 'text-[8px]';
    default:
      size satisfies never;
  }

  return 'numeric-medium';
};

export const thumbnailVariants = cva('flex justify-center items-center', {
  variants: {
    appearance: {
      square: '',
      circle: 'rounded-[50%]',
    },
    size: {
      xxxlg: '!w-96 !h-96',
      xxlg: 'w-64 h-64',
      xlg: 'w-48 h-48',
      lg: 'w-32 h-32',
      md: 'w-24 h-24',
      sm: 'w-20 h-20',
      xsm: 'w-16 h-16',
      xxsm: 'w-12 h-12',
    },
    hasPadding: {
      true: 'p-16',
      false: 'p-0',
    },
  },
  compoundVariants: [
    {
      size: 'xxxlg',
      hasPadding: true,
      class: 'p-16',
    },
    {
      size: 'xxlg',
      hasPadding: true,
      class: 'p-8',
    },
    {
      size: 'xlg',
      hasPadding: true,
      class: 'p-8',
    },
    {
      size: 'lg',
      hasPadding: true,
      class: 'p-4',
    },
    {
      size: 'md',
      hasPadding: true,
      class: 'p-2',
    },
    {
      size: 'sm',
      hasPadding: true,
      class: 'p-2',
    },
    {
      size: 'xsm',
      hasPadding: true,
      class: 'p-2',
    },
    {
      size: 'xxsm',
      hasPadding: true,
      class: 'p-2',
    },
    {
      size: 'xxxlg',
      appearance: 'square',
      class: 'rounded-24',
    },
    {
      size: 'xxlg',
      appearance: 'square',
      class: 'rounded-16',
    },
    {
      size: 'xlg',
      appearance: 'square',
      class: 'rounded-12',
    },
    {
      size: 'lg',
      appearance: 'square',
      class: 'rounded-8',
    },
    {
      size: 'md',
      appearance: 'square',
      class: 'rounded-6',
    },
    {
      size: 'sm',
      appearance: 'square',
      class: 'rounded-6',
    },
    {
      size: 'xsm',
      appearance: 'square',
      class: 'rounded-4',
    },
    {
      size: 'xxsm',
      appearance: 'square',
      class: 'rounded-4',
    },
  ],
  defaultVariants: {
    size: 'md',
    hasPadding: true,
  },
});
